// QueuePauseBanner — AppShell altında sticky banner.
// Yalnızca admin'lere görünür; system.queue_paused=true iken "Üretim duraklatıldı"
// + reason + "Devam et" CTA gösterir. Resume endpoint'ini çağırır.

import { useEffect, useState, useCallback } from "react";
import { api } from "../../api/client";
import { useAuthStore } from "../../stores/auth-store";

interface QueueStatus {
  paused: boolean;
  reason: "auth" | "manual" | "budget" | null;
}

export function QueuePauseBanner(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.permissions["system.admin"] === true;

  const [status, setStatus] = useState<QueueStatus>({ paused: false, reason: null });
  const [resuming, setResuming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async (): Promise<void> => {
    if (!isAdmin) return;
    try {
      const res = await api<QueueStatus>("/admin/queue/status");
      setStatus(res);
    } catch (err) {
      // sessiz geç — admin olmayan kullanıcı veya geçici 403/500
      setError(err instanceof Error ? err.message : null);
    }
  }, [isAdmin]);

  useEffect(() => {
    void fetchStatus();
    // 30sn'de bir re-poll (notification SSE'den de indirect olarak güncellenir
    // ama banner'ı hep taze tut)
    const id = setInterval(() => void fetchStatus(), 30_000);
    return () => {
      clearInterval(id);
    };
  }, [fetchStatus]);

  const onResume = async (): Promise<void> => {
    setResuming(true);
    setError(null);
    try {
      await api("/admin/queue/resume", { method: "POST" });
      await fetchStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : "İşlem başarısız");
    } finally {
      setResuming(false);
    }
  };

  if (!isAdmin || !status.paused) return null;

  const { label, detail } = describe(status.reason);

  return (
    <div
      role="alert"
      style={{
        background: "color-mix(in srgb, var(--color-warning) 14%, white)",
        borderBottom: "1px solid var(--color-warning)",
        color: "var(--color-warning)",
        padding: "10px 20px",
        display: "flex",
        alignItems: "center",
        gap: 16,
        fontSize: "0.88rem",
      }}
    >
      <span style={{ fontWeight: 700 }}>⚠ {label}</span>
      <span style={{ flex: 1, color: "var(--color-warning)" }}>{detail}</span>
      {error && <span style={{ color: "var(--color-error)", fontSize: "0.8rem" }}>{error}</span>}
      <button
        onClick={() => void onResume()}
        disabled={resuming}
        style={{
          padding: "6px 14px",
          borderRadius: 6,
          border: "1px solid var(--color-warning)",
          background: resuming
            ? "color-mix(in srgb, var(--color-warning) 30%, white)"
            : "var(--color-warning)",
          color: "var(--color-surface)",
          fontWeight: 600,
          fontSize: "0.82rem",
          cursor: resuming ? "not-allowed" : "pointer",
        }}
      >
        {resuming ? "Devam ettiriliyor…" : "Üretimi Devam Ettir"}
      </button>
    </div>
  );
}

function describe(reason: QueueStatus["reason"]): { label: string; detail: string } {
  if (reason === "auth") {
    return {
      label: "Üretim duraklatıldı — LLM kimlik doğrulama hatası (E1)",
      detail:
        "Sistem Ayarları → LLM'den API anahtarını kontrol edin, ardından Devam Ettir'e basın.",
    };
  }
  if (reason === "budget") {
    return {
      label: "Üretim duraklatıldı — aylık bütçe aşıldı",
      detail: "Bütçeyi Sistem Ayarları'ndan yükseltip kuyruğu devam ettirebilirsiniz.",
    };
  }
  return {
    label: "Üretim duraklatıldı (manuel)",
    detail: "Kuyruk bir admin tarafından duraklatıldı. Devam Ettir'e basın.",
  };
}
